import { Integration, WithId } from '@betterfunnel/types';
import Badge from '@betterfunnel/ui-core/Badge';
import Button from '@betterfunnel/ui-core/Button';
import MenuButton from '@betterfunnel/ui-core/MenuButton';
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  DocumentDuplicateIcon,
  LockClosedIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { ColumnDef } from '@tanstack/react-table';
import { diff } from 'deep-diff';
import { Timestamp } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CustomDataGrid } from '../components/CustomDataGrid/CustomDataGrid';
import { useAuditLog } from '../contexts/AuditLogContext';
import { useDashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { EditWebhookIntegrationModal } from '../modals/EditWebhookIntegrationModal';
import {
  duplicateWebhookIntegration,
  getIntegrations,
  updateIntegration,
} from '../services/IntegrationsService';
import { capitalize } from '../utils';

type IntegrationItem = WithId<Integration>;

interface Props {
  dynamicPageId?: string;
}

export default function IntegrationsTable({ dynamicPageId }: Props) {
  const { show, hide } = useSnackbarContext();
  const { selectedAccount, role } = useContext(UserContext);
  const { dynamicPages } = useDashboardContext();
  const auditLog = useAuditLog();

  const [integrations, setIntegrations] = useState<IntegrationItem[]>();
  const [addIntegrationModalOpen, setAddIntegrationModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<
    IntegrationItem | undefined
  >(undefined);

  const canEditIntegrations = useMemo(() => {
    return role === 'admin';
  }, [role]);

  const fetchIntegrations = useCallback(() => {
    if (!selectedAccount) return;

    getIntegrations(selectedAccount.id, dynamicPageId)
      .then((integrations) => {
        setIntegrations(integrations);
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, [selectedAccount, dynamicPageId]);

  useEffect(() => {
    setIntegrations(undefined);

    if (!selectedAccount) return;

    fetchIntegrations();
  }, [selectedAccount, dynamicPageId, fetchIntegrations]);

  const onDuplicateIntegration = useCallback(
    (integrationId: string) => {
      const snack = show('Duplicating integration...');

      duplicateWebhookIntegration(selectedAccount!.id, integrationId)
        .then(() => {
          show('Integration duplicated successfully');
          fetchIntegrations();

          auditLog.log(
            'create',
            'Duplicate integration',
            'integration',
            integrationId,
          );
        })
        .catch((error) => {
          show('Error duplicating integration');
          Sentry.captureException(error);
        })
        .finally(() => {
          hide(snack.id);
        });
    },
    [auditLog, fetchIntegrations, hide, selectedAccount, show],
  );

  const onToggleIntegrationStatus = useCallback(
    (integration: IntegrationItem, newStatus: Integration['status']) => {
      updateIntegration(selectedAccount!.id, integration.id, {
        status: newStatus,
      })
        .then(() => {
          show('Integration is now ' + capitalize(newStatus));
          fetchIntegrations();
          auditLog.log(
            'update',
            'Update integration status',
            'integration',
            integration.id,
            undefined,
            diff({ status: integration.status }, { status: newStatus }),
          );
        })
        .catch((error) => {
          show('Error updating integration status');
          Sentry.captureException(error);
        });
    },
    [auditLog, fetchIntegrations, selectedAccount, show],
  );

  const onDeleteIntegration = useCallback(
    (integration: IntegrationItem) => {
      updateIntegration(selectedAccount!.id, integration.id, {
        status: 'deleted',
      })
        .then(() => {
          show('Integration deleted successfully');
          fetchIntegrations();
          auditLog.log(
            'delete',
            'Delete integration',
            'integration',
            integration.id,
          );
        })
        .catch((error) => {
          show('Error deleting integration');
          Sentry.captureException(error);
        });
    },
    [auditLog, fetchIntegrations, selectedAccount, show],
  );

  const columns: ColumnDef<IntegrationItem>[] = useMemo(() => {
    return [
      {
        header: 'Name',
        id: 'name',
        accessorKey: 'name',
        size: 300,
      },
      {
        header: 'Status',
        id: 'status',
        accessorKey: 'status',
        size: 120,
        cell: (info) => (
          <Badge
            hideDot
            rounded={false}
            size="small"
            showBorder
            title={capitalize(info.row.original.status)}
            color={info.getValue() === 'active' ? 'green' : 'red'}
          />
        ),
      },
      {
        header: 'Type',
        id: 'type',
        accessorKey: 'type',
        size: 250,
        cell: (info) =>
          info.getValue() === 'new_lead'
            ? 'On New Lead'
            : `On Event: ${(info.row.original as any).eventTypes?.join(', ')}`,
      },
      {
        header: 'Authentication',
        id: 'authentication',
        accessorFn: (row) =>
          ('authentication' in row ? row.authentication?.type : undefined) ===
          'apiKey'
            ? 'API Key'
            : 'Public/Private Key',
        size: 180,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <LockClosedIcon className="size-4 text-gray-500" />
            {info.getValue() as string}
          </div>
        ),
      },
      {
        header: 'Created At',
        id: 'createdAt',
        accessorFn: (row) =>
          row.createdAt
            ? new Timestamp(
                row.createdAt._seconds,
                row.createdAt._nanoseconds,
              ).toDate()
            : undefined,
        size: 180,
        cell: (info) => info.getValue()?.toLocaleString(),
      },
      ...(dynamicPageId
        ? []
        : ([
            {
              header: 'Dynamic Pages',
              id: 'dynamicPageIds',
              accessorFn: (row) =>
                'dynamicPageIds' in row && row.dynamicPageIds?.length
                  ? row.dynamicPageIds
                      .map((id) => dynamicPages?.find((p) => p.id === id))
                      .filter((x) => !!x)
                  : undefined,
              size: 180,
              cell: (info) => (
                <div>
                  <div className="flex flex-wrap gap-1">
                    {(info.getValue() as any)?.map((x: any) => (
                      <Badge
                        key={x.id}
                        title={x.data().name}
                        hideDot
                        size="small"
                      />
                    ))}
                  </div>
                </div>
              ),
            },
          ] satisfies ColumnDef<IntegrationItem>[])),
      ...(canEditIntegrations
        ? ([
            {
              header: '',
              id: 'actions',
              size: 100,
              cell: ({ row }) => {
                // const integration = info.row.original;
                return (
                  <MenuButton
                    // buttonClassName="visible-on-row-hover"
                    items={[
                      {
                        title: 'Edit',
                        icon: PencilIcon,
                        onClick: () => {
                          setSelectedIntegration(row.original);
                          setAddIntegrationModalOpen(true);
                        },
                      },
                      {
                        title: 'Duplicate',
                        icon: DocumentDuplicateIcon,
                        onClick: () => onDuplicateIntegration(row.original.id),
                      },
                      {
                        title:
                          row.original.status !== 'active'
                            ? 'Enable'
                            : 'Disable',
                        icon:
                          row.original.status !== 'active'
                            ? ArrowUpCircleIcon
                            : ArrowDownCircleIcon,
                        onClick: () =>
                          onToggleIntegrationStatus(
                            row.original,
                            row.original.status === 'active'
                              ? 'disabled'
                              : 'active',
                          ),
                      },
                      {
                        title: 'Delete',
                        icon: TrashIcon,
                        destructive: true,
                        onClick: () => onDeleteIntegration(row.original),
                      },
                    ]}
                  />
                );
              },
            },
          ] satisfies ColumnDef<IntegrationItem>[])
        : []),
    ] satisfies ColumnDef<IntegrationItem>[];
  }, [
    canEditIntegrations,
    dynamicPageId,
    dynamicPages,
    onDeleteIntegration,
    onDuplicateIntegration,
    onToggleIntegrationStatus,
  ]);

  return (
    <>
      <div className="flex h-full flex-col py-8">
        <EditWebhookIntegrationModal
          open={addIntegrationModalOpen}
          onClose={() => {
            setAddIntegrationModalOpen(false);
            setSelectedIntegration(undefined);
          }}
          onSaved={() => {
            fetchIntegrations();
          }}
          integrationId={selectedIntegration?.id}
          integration={
            selectedIntegration?.provider === 'webhook'
              ? selectedIntegration
              : undefined
          }
          forceDynamicPagesConnection={
            dynamicPageId ? [dynamicPageId] : undefined
          }
        />
        <div className="pb-8 sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base leading-6 font-semibold text-gray-900">
              Integrations
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage your integrations with other services.
            </p>
          </div>
          {canEditIntegrations && (
            <Button
              icon={<PlusIcon className="h-4 w-4 text-white" />}
              onClick={() => {
                setSelectedIntegration(undefined);
                setAddIntegrationModalOpen(true);
              }}
            >
              Add Integration
            </Button>
          )}
        </div>
        <div className="relative flex-1">
          <CustomDataGrid
            logicalName="integrations"
            columns={columns}
            data={integrations}
            rowClassName={'min-h-16'}
            virtualized={false}
            initialState={{
              columnPinning: { right: ['actions'] },
            }}
          />
        </div>
      </div>
    </>
  );
}
